import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  useState(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: 'How does SERGAS VPN protect my privacy?',
      answer: 'SERGAS VPN uses military-grade AES-256 encryption to secure your internet connection. We implement a strict zero-log policy, meaning we never track, collect, or share your online activities, browsing history, or personal information. Your data remains completely private and secure.'
    },
    {
      question: 'What devices are compatible with SERGAS VPN?',
      answer: 'SERGAS VPN is compatible with all major platforms including Windows, macOS, Linux, iOS, and Android. You can also configure it on routers to protect all devices on your network simultaneously.'
    },
    {
      question: 'How many devices can I connect simultaneously?',
      answer: 'With a single SERGAS VPN subscription, you can connect up to 10 devices simultaneously. This allows you to protect all your devices - smartphones, tablets, laptops, and desktops - with one account.'
    },
    {
      question: 'Do you offer customer support?',
      answer: 'Yes, we provide 24/7 customer support through email and phone. Our dedicated team is always ready to assist you with any questions or technical issues you may encounter.'
    },
    {
      question: 'What is your refund policy?',
      answer: 'We offer a 30-day money-back guarantee. If you are not satisfied with our service for any reason, you can request a full refund within 30 days of your purchase, no questions asked.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/cf8337a9-e4c6-426c-ab9b-76767bc07ef2.png" 
                alt="SERGAS Logo" 
                className="w-12 h-12 object-contain"
              />
              <span className={`text-2xl font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>SERGAS</span>
            </div>
            <div className="flex items-center gap-8">
              <a href="#home" className={`text-sm font-medium uppercase tracking-wide hover:opacity-70 transition-opacity whitespace-nowrap ${isScrolled ? 'text-gray-900' : 'text-white'}`}>Home</a>
              <a href="#about" className={`text-sm font-medium uppercase tracking-wide hover:opacity-70 transition-opacity whitespace-nowrap ${isScrolled ? 'text-gray-900' : 'text-white'}`}>About</a>
              <a href="#services" className={`text-sm font-medium uppercase tracking-wide hover:opacity-70 transition-opacity whitespace-nowrap ${isScrolled ? 'text-gray-900' : 'text-white'}`}>VPN Services</a>
              <a href="#contact" className={`text-sm font-medium uppercase tracking-wide hover:opacity-70 transition-opacity whitespace-nowrap ${isScrolled ? 'text-gray-900' : 'text-white'}`}>Contact</a>
              <a href="#faq" className={`text-sm font-medium uppercase tracking-wide hover:opacity-70 transition-opacity whitespace-nowrap ${isScrolled ? 'text-gray-900' : 'text-white'}`}>FAQ</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/a4283e8f174e3352d1221da9e518e0e8.jpg" 
            alt="Hero Background" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="max-w-2xl">
            <h1 className="text-7xl font-bold text-white mb-6 leading-tight">
              Secure Your<br />
              <span className="font-extrabold">Digital Privacy</span>
            </h1>
            <p className="text-2xl text-white/90 mb-10 leading-relaxed">
              Enterprise-grade VPN protection for your online activities
            </p>
            <a 
              href="#contact" 
              className="inline-flex items-center gap-3 bg-[#1A2B4A] text-white px-10 py-4 rounded-full text-base font-semibold hover:bg-[#2A3B5A] transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              GET STARTED
              <i className="ri-arrow-right-line text-xl"></i>
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block border border-gray-300 rounded-full px-5 py-2 mb-6">
                <span className="text-xs uppercase tracking-wider text-gray-600">WHO WE ARE</span>
              </div>
              <div className="rounded-3xl overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20technology%20company%20office%20workspace%20with%20sleek%20computers%20and%20digital%20screens%20showing%20network%20security%20visualizations%2C%20professional%20team%20collaboration%20environment%2C%20clean%20contemporary%20interior%20design%20with%20glass%20walls%20and%20minimalist%20furniture%2C%20bright%20natural%20lighting%2C%20corporate%20tech%20atmosphere%20with%20blue%20and%20white%20color%20scheme&width=600&height=800&seq=about-img-001&orientation=portrait" 
                  alt="About SERGAS" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            
            <div>
              <h2 className="text-5xl font-extrabold text-gray-900 mb-8 leading-tight">
                Protecting Your<br />
                Online Freedom<br />
                Since Day One
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                SERGAS S.R.L. specializes in developing cutting-edge VPN solutions that prioritize user privacy and security. Our commitment is to provide reliable, fast, and secure internet access for individuals and businesses worldwide.
              </p>
              <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                Based in San Carlos de Bariloche, Argentina, we combine advanced encryption technology with a user-friendly experience to ensure your digital activities remain private and protected at all times.
              </p>
              <a 
                href="#services" 
                className="inline-flex items-center gap-3 bg-gray-900 text-white px-10 py-4 rounded-full text-base font-semibold hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer"
              >
                LEARN MORE
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* VPN Services Section */}
      <section id="services" className="py-24 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-wider text-gray-500 mb-4 block">OUR SOLUTION</span>
            <h2 className="text-6xl font-extrabold text-gray-900 leading-tight">
              Why Choose<br />
              SERGAS VPN
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-[#F5F5F5] rounded-2xl p-10">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-5xl text-gray-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Military-Grade Encryption</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                AES-256 encryption protects your data from unauthorized access and cyber threats
              </p>
            </div>
            
            <div className="bg-[#F0F8F5] rounded-2xl p-10">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-flashlight-line text-5xl text-gray-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Lightning Fast Servers</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Optimized global server network ensures minimal speed loss while maintaining security
              </p>
            </div>
            
            <div className="bg-[#F5F3F8] rounded-2xl p-10">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-eye-off-line text-5xl text-gray-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Zero-Log Policy</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                We never track, collect, or share your online activities or personal information
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-wider text-gray-500 mb-4 block">GET IN TOUCH</span>
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Contact Our Team</h2>
          </div>
          
          <div className="space-y-6 mb-12">
            <div className="bg-gray-50 rounded-xl p-6 flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center">
                <i className="ri-mail-line text-2xl text-gray-700"></i>
              </div>
              <a href="mailto:assistance@sergas-srl.com" className="text-lg font-medium text-gray-900 hover:text-gray-700 transition-colors cursor-pointer">
                assistance@sergas-srl.com
              </a>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-6 flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center">
                <i className="ri-phone-line text-2xl text-gray-700"></i>
              </div>
              <a href="tel:+18507793225" className="text-lg font-medium text-gray-900 hover:text-gray-700 transition-colors cursor-pointer">
                +1 850 779 3225
              </a>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-6 flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center">
                <i className="ri-map-pin-line text-2xl text-gray-700"></i>
              </div>
              <div className="text-lg font-medium text-gray-900">
                <p>Vicalte Eduardo O'Connor 665</p>
                <p>San Carlos de Bariloche, R8400ACI</p>
                <p>Argentina</p>
              </div>
            </div>
          </div>
          
          <form 
            id="contact-form" 
            data-readdy-form 
            action="https://readdy.ai/api/form/d56hem0f3qcgnii4i730" 
            method="POST"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const formData = new FormData(form);
              const data = new URLSearchParams();
              
              formData.forEach((value, key) => {
                data.append(key, value.toString());
              });
              
              fetch(form.action, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: data.toString()
              })
              .then(response => {
                if (response.ok) {
                  alert('Message sent successfully! We will get back to you soon.');
                  form.reset();
                } else {
                  alert('Failed to send message. Please try again.');
                }
              })
              .catch(() => {
                alert('Failed to send message. Please try again.');
              });
            }}
            className="space-y-6"
          >
            <div>
              <input 
                type="text" 
                name="name" 
                placeholder="Your Name" 
                required
                className="w-full px-6 py-4 bg-gray-50 border border-gray-200 rounded-xl text-base focus:outline-none focus:border-gray-400 transition-colors"
              />
            </div>
            <div>
              <input 
                type="email" 
                name="email" 
                placeholder="Your Email" 
                required
                className="w-full px-6 py-4 bg-gray-50 border border-gray-200 rounded-xl text-base focus:outline-none focus:border-gray-400 transition-colors"
              />
            </div>
            <div>
              <textarea 
                name="message" 
                placeholder="Your Message" 
                rows={6}
                maxLength={500}
                required
                className="w-full px-6 py-4 bg-gray-50 border border-gray-200 rounded-xl text-base focus:outline-none focus:border-gray-400 transition-colors resize-none"
              ></textarea>
              <p className="text-sm text-gray-500 mt-2">Maximum 500 characters</p>
            </div>
            <button 
              type="submit" 
              className="w-full bg-gray-900 text-white py-4 rounded-xl text-base font-semibold hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer"
            >
              SEND MESSAGE
            </button>
          </form>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-wider text-gray-500 mb-4 block">COMMON QUESTIONS</span>
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Find answers to common inquiries about SERGAS VPN</p>
          </div>
          
          <div className="space-y-0">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-gray-200">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full py-8 flex items-center justify-between text-left hover:bg-gray-50 transition-colors px-4 cursor-pointer"
                >
                  <span className="text-xl font-bold text-gray-900 pr-8">{faq.question}</span>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className={`${openFaq === index ? 'ri-subtract-line' : 'ri-add-line'} text-2xl text-gray-900`}></i>
                  </div>
                </button>
                {openFaq === index && (
                  <div className="px-4 pb-8">
                    <p className="text-base text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A3A2E] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img 
                  src="https://public.readdy.ai/ai/img_res/cf8337a9-e4c6-426c-ab9b-76767bc07ef2.png" 
                  alt="SERGAS Logo" 
                  className="w-10 h-10 object-contain brightness-0 invert"
                />
                <span className="text-xl font-bold">SERGAS</span>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">
                Enterprise VPN solutions<br />
                for modern businesses<br />
                and individuals
              </p>
            </div>
            
            <div>
              <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-6">STAY UPDATED</h4>
              <form 
                id="newsletter-form" 
                data-readdy-form 
                action="https://readdy.ai/api/form/d56hem0f3qcgnii4i730" 
                method="POST"
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  const data = new URLSearchParams();
                  
                  formData.forEach((value, key) => {
                    data.append(key, value.toString());
                  });
                  
                  fetch(form.action, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: data.toString()
                  })
                  .then(response => {
                    if (response.ok) {
                      alert('Successfully subscribed to newsletter!');
                      form.reset();
                    } else {
                      alert('Failed to subscribe. Please try again.');
                    }
                  })
                  .catch(() => {
                    alert('Failed to subscribe. Please try again.');
                  });
                }}
                className="mb-3"
              >
                <div className="flex items-center border-b border-gray-500 pb-2">
                  <input 
                    type="email" 
                    name="email" 
                    placeholder="Enter your email" 
                    required
                    className="flex-1 bg-transparent text-white placeholder-gray-400 text-sm focus:outline-none"
                  />
                  <button type="submit" className="ml-2 cursor-pointer">
                    <i className="ri-arrow-right-line text-xl text-white"></i>
                  </button>
                </div>
              </form>
              <p className="text-xs text-gray-400">Subscribe to our newsletter</p>
            </div>
            
            <div>
              <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-6">QUICK LINKS</h4>
              <div className="space-y-3">
                <a href="#home" className="block text-base text-white hover:text-gray-300 transition-colors cursor-pointer">Home</a>
                <a href="#services" className="block text-base text-white hover:text-gray-300 transition-colors cursor-pointer">VPN Services</a>
                <a href="#about" className="block text-base text-white hover:text-gray-300 transition-colors cursor-pointer">About Us</a>
                <a href="#contact" className="block text-base text-white hover:text-gray-300 transition-colors cursor-pointer">Contact</a>
              </div>
            </div>
            
            <div>
              <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-6">LEGAL</h4>
              <div className="space-y-3">
                <a href="#" className="block text-base text-white hover:text-gray-300 transition-colors cursor-pointer">Privacy Policy</a>
                <a href="#" className="block text-base text-white hover:text-gray-300 transition-colors cursor-pointer">Terms of Service</a>
                <a href="#" className="block text-base text-white hover:text-gray-300 transition-colors cursor-pointer">Cookie Policy</a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-600 pt-12">
            <div className="text-center">
              <h3 className="text-8xl font-extrabold text-[#E8DCC4] tracking-tight">SERGAS</h3>
            </div>
          </div>
          
          <div className="mt-12 text-center text-sm text-gray-400">
            <p>&copy; 2025 SERGAS S.R.L. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="inline-block mt-2 hover:text-gray-300 transition-colors cursor-pointer">Powered by Readdy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
